import { useState, useCallback } from "react";
import type { LayoutChangeEvent } from "react-native";
import { Text, View } from "@src/components";
import type { WasteSenseReportMeta, WasteSenseKpis } from "@visioni/a3s-reports/client-messages";
import { KpiCard } from "./WeeklySummary";

/**
 * Width (px) of the hero container below which the title/dates column and the
 * KPI card stack into a single left-aligned column. Measured against the hero's
 * OWN width (via onLayout), not the browser window — the report sits inside a
 * layout with a sidebar and optional right panel, so the window width tells us
 * nothing useful about how much room this block actually has.
 *
 * Set low (420px) on purpose: the title is short ("Waste Sense") and the card
 * shrinks to fit its label, so the two only need to stack when the container is
 * genuinely narrow — otherwise they sit comfortably side by side.
 */
const STACK_BELOW_WIDTH = 420;

/**
 * Product title shown at the top of the report. Hardcoded here (rather than
 * taken from `report_meta.report_title`) because it's a fixed brand name — the
 * generator's title string is a longer descriptive label used elsewhere.
 */
const REPORT_TITLE = "Waste Sense";

interface WasteSenseHeroProps {
  meta: WasteSenseReportMeta;
  /** KPI counts — the hero surfaces the confirmed-detections total as a card. */
  kpis: WasteSenseKpis;
}

/**
 * Top-of-report header block. The left column carries the title and the
 * reporting-window dates; the right column surfaces the headline
 * "Confirmed Detections" count as a KPI card matching the "Detections
 * processed" tiles below.
 */
export function WasteSenseHero({ meta, kpis }: WasteSenseHeroProps) {
  // Measure the hero's actual rendered width so the breakpoint is a true
  // container query. `undefined` until the first layout pass — we default to
  // the wide layout in that window because the hero renders full-width and only
  // collapses once measured narrow, which avoids a flash of the stacked layout
  // in the common desktop case.
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  // Stack (title/dates above the card, all left-aligned) once the container is
  // narrower than the threshold. When stacked we also drop the type scale a
  // step and tighten the line gap so the taller single column doesn't dominate.
  const isStacked = containerWidth !== undefined && containerWidth < STACK_BELOW_WIDTH;

  const titleStyle = isStacked ? "h4" : "h3";
  const lineStyle = isStacked ? "xs/regular" : "sm/regular";
  const lineGap = isStacked ? "xs" : "sm";

  return (
    <View
      onLayout={onLayout}
      // Row on wide containers (title/dates left, KPI card right); column when
      // stacked so everything flows down.
      flexDirection={isStacked ? "column" : "row"}
      gap={isStacked ? "md" : "3xl"}
      alignItems="flex-start"
      // Full width so onLayout reports the container's real width, and so the
      // flex-push below has the full row to spread across.
      width="100%"
    >
      <View gap={lineGap} alignItems="flex-start" flex={isStacked ? undefined : 1}>
        <Text textStyle={titleStyle} color="text">
          {REPORT_TITLE}
        </Text>
        <Text textStyle={lineStyle} color="textMuted">
          {meta.window_label}
        </Text>
      </View>
      {/* Confirmed-detections KPI card. Reuses KpiCard with hero-specific
          overrides: tighter 10px padding, right-aligned value, and shrink-to-fit
          width (fill=false) so it's only as wide as the "Confirmed Detections"
          label rather than a fixed 200px block. */}
      <KpiCard
        label="Confirmed Detections"
        value={kpis.confirmed_dumps.toLocaleString()}
        padding={10}
        valueAlign="right"
        fill={false}
        gap="none"
      />
    </View>
  );
}
